<script setup lang="ts">
import { useBreakpoints } from "@vueuse/core";
import EventusEnterpriseHeader from "../../../assets/image/eventUs/eventUs-enterprise-header.jpg";
import IconEventusEnterprise from "../../../assets/image/eventUs/icons/icon-eventUs-enterprise.svg";
import GoldTitle from "../../../assets/image/eventUs/icons/enterprise/gold-title.svg";
import GoldKart from "../../../assets/image/eventUs/icons/enterprise/gold-kart.svg";
import GoldDrink from "../../../assets/image/eventUs/icons/enterprise/gold-drink.svg";
import GoldFood from "../../../assets/image/eventUs/icons/enterprise/gold-food.svg";
import GoldReception from "../../../assets/image/eventUs/icons/enterprise/gold-reception.svg";
import SilverTitle from "../../../assets/image/eventUs/icons/enterprise/silver-title.svg";
import SilverKart from "../../../assets/image/eventUs/icons/enterprise/silver-kart.svg";
import SilverDrink from "../../../assets/image/eventUs/icons/enterprise/silver-drink.svg";
import SilverFood from "../../../assets/image/eventUs/icons/enterprise/silver-food.svg";
import SilverReception from "../../../assets/image/eventUs/icons/enterprise/silver-reception.svg";
import BronzeTitle from "../../../assets/image/eventUs/icons/enterprise/bronze-title.svg";
import BronzeKart from "../../../assets/image/eventUs/icons/enterprise/bronze-kart.svg";
import BronzeDrink from "../../../assets/image/eventUs/icons/enterprise/bronze-drink.svg";
import BronzeFood from "../../../assets/image/eventUs/icons/enterprise/bronze-food.svg";
import BronzeReception from "../../../assets/image/eventUs/icons/enterprise/bronze-reception.svg";
import RacerTitle from "../../../assets/image/eventUs/icons/enterprise/racer-title.svg";
import RacerKart from "../../../assets/image/eventUs/icons/enterprise/racer-kart.svg";
import RacerDrink from "../../../assets/image/eventUs/icons/enterprise/racer-drink.svg";
import RacerReception from "../../../assets/image/eventUs/icons/enterprise/racer-reception.svg";
import EventUsNavigation from "../../../components/website/EventUsNavigation.vue";
import ImageHeader from "../../../components/website/ImageHeader.vue";
import Who from "../../../components/website/LayoutForWho.vue";
import { useTranslate } from "addeus-common-library/stores/translate";
import { ref } from "vue";
import VGallery from "addeus-common-library/components/base/gallery/VGallery.vue";
import PackCard from "../../../components/website/PackCard.vue";
import VGalleryPage from "addeus-common-library/components/base/gallery/VGalleryPage.vue";
import TranslateNamespace from "addeus-common-library/components/base/translation/TranslateNamespace.vue";
import type { PackInfoBlockProp } from "../../../components/website/PackInfoBlock.vue";
import PackInfoBlock from "../../../components/website/PackInfoBlock.vue";
import {
    ButtonColor,
    ButtonSize,
    TextBoldness,
} from "../../../components/website/SimpleButton.vue";
import { useOwner } from "../../../stores/website/owner";
import { storeToRefs } from "pinia";
import { useSeoMeta } from "../../../composables/useSeoMeta";

const { setTranslateNamespace, translate } = useTranslate();
setTranslateNamespace(".eventUs.enterprise");

useSeoMeta({
    title: "Séminaire et team building karting électrique",
    description:
        "Séminaire karting électrique entreprise : team building écoresponsable zéro émission. Formules Gold, Silver, Bronze avec piste privatisée, restauration et podium. Devis personnalisé, réseau multi-sites France.",
    path: "/event-us/enterprise",
    breadcrumbs: [
        { name: "Accueil", path: "/" },
        { name: "Événements chez nous", path: "/event-us" },
        { name: "Entreprise", path: "/event-us/enterprise" },
    ],
    schemaOrg: [
        {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Séminaire et team building karting électrique - Speed2Max",
            description:
                "Séminaire karting électrique écoresponsable pour entreprises. Formules Gold, Silver, Bronze, Racer avec piste privatisée et restauration.",
            url: "https://speed2max.com/event-us/enterprise",
            provider: {
                "@type": "Organization",
                name: "Speed2Max",
                url: "https://speed2max.com",
            },
            serviceType: "Séminaire et team building karting électrique",
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
                {
                    "@type": "Question",
                    name: "Quelles sont les formules team building karting disponibles ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "Speed2Max propose 4 formules entreprise : Gold (la plus complète avec repas), Silver, Bronze et Racer (la plus sportive). Chaque formule inclut 3 runs, piste privatisée et podium.",
                    },
                },
                {
                    "@type": "Question",
                    name: "Les formules entreprise incluent-elles la restauration ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "Les formules Gold et Silver incluent un service de restauration complet. La formule Bronze propose un buffet simplifié. La formule Racer inclut une consommation.",
                    },
                },
                {
                    "@type": "Question",
                    name: "Comment réserver un team building karting ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "Contactez-nous via le formulaire de la page pour obtenir un devis personnalisé. Nous adaptons chaque événement à vos besoins (nombre de participants, budget, horaires).",
                    },
                },
            ],
        },
    ],
});
const breakpoints = useBreakpoints({ sm: 700 });
const smallerThanSm = breakpoints.smallerOrEqual("sm");

const { owner } = storeToRefs(useOwner());

const isModalContactOpen = ref(false);
const contactBlock = ref(undefined);
function needContact(pack) {
    contactBlock.value = pack;
    isModalContactOpen.value = true;
}
const goldInfoBlocks: PackInfoBlockProp[] = [
    {
        color: "gold",
        icon: GoldKart,
        title: translate(".pack.gold.kart.title").value,
        items: [
            { text: translate(".pack.gold.kart.feature").value, hideDot: true },
            {
                spec: translate(".pack.gold.kart.run1").value,
                text: translate(".pack.gold.kart.desc1").value,
            },
            {
                spec: translate(".pack.gold.kart.run2").value,
                text: translate(".pack.gold.kart.desc2").value,
            },
            {
                spec: translate(".pack.gold.kart.run3").value,
                text: translate(".pack.gold.kart.desc3").value,
            },
        ],
    },
    {
        color: "gold",
        icon: GoldDrink,
        title: translate(".pack.gold.drink.title").value,
        items: [
            { text: translate(".pack.gold.drink.desc1").value },
            { text: translate(".pack.gold.drink.desc2").value },
            { text: translate(".pack.gold.drink.desc3").value },
            { text: translate(".pack.gold.drink.desc4").value },
        ],
    },
    {
        color: "gold",
        icon: GoldFood,
        title: translate(".pack.gold.food.title").value,
        items: [
            { spec: translate(".pack.gold.food.desc1").value, hideDot: true },
            { text: translate(".pack.gold.food.desc2").value },
            { text: translate(".pack.gold.food.desc3").value },
        ],
    },
    {
        color: "gold",
        icon: GoldReception,
        title: translate(".pack.gold.reception.title").value,
        items: [
            { text: translate(".pack.gold.reception.desc2").value },
            { text: translate(".pack.gold.reception.desc3").value },
            { text: translate(".pack.gold.reception.desc1").value },
        ],
    },
];
const silverInfoBlocks: PackInfoBlockProp[] = [
    {
        color: "silver",
        icon: SilverKart,
        title: translate(".pack.silver.kart.title").value,
        items: [
            { text: translate(".pack.silver.kart.feature").value, hideDot: true },
            {
                spec: translate(".pack.silver.kart.run1").value,
                text: translate(".pack.silver.kart.desc1").value,
            },
            {
                spec: translate(".pack.silver.kart.run2").value,
                text: translate(".pack.silver.kart.desc2").value,
            },
            {
                spec: translate(".pack.silver.kart.run3").value,
                text: translate(".pack.silver.kart.desc3").value,
            },
        ],
    },
    {
        color: "silver",
        icon: SilverDrink,
        title: translate(".pack.silver.drink.title").value,
        items: [
            { text: translate(".pack.silver.drink.desc1").value },
            { text: translate(".pack.silver.drink.desc2").value },
            { text: translate(".pack.silver.drink.desc3").value },
            { text: translate(".pack.silver.drink.desc4").value },
        ],
    },
    {
        color: "silver",
        icon: SilverFood,
        title: translate(".pack.silver.food.title").value,
        items: [
            {
                spec: translate(".pack.silver.food.desc1").value,
                hideDot: true,
                boldness: "bold",
            },
            {
                spec: translate(".pack.silver.food.desc2").value,
                hideDot: true,
                boldness: "bold",
            },
            { text: translate(".pack.silver.food.desc3").value },
            { text: translate(".pack.silver.food.desc4").value },
        ],
    },
    {
        color: "silver",
        icon: SilverReception,
        title: translate(".pack.silver.reception.title").value,
        items: [
            { text: translate(".pack.silver.reception.desc2").value },
            { text: translate(".pack.silver.reception.desc3").value },
            { text: translate(".pack.silver.reception.desc1").value },
        ],
    },
];
const bronzeInfoBlocks: PackInfoBlockProp[] = [
    {
        color: "bronze",
        icon: BronzeKart,
        title: translate(".pack.bronze.kart.title").value,
        items: [
            { text: translate(".pack.bronze.kart.feature").value, hideDot: true },
            {
                spec: translate(".pack.bronze.kart.run1").value,
                text: translate(".pack.bronze.kart.desc1").value,
            },
            {
                spec: translate(".pack.bronze.kart.run2").value,
                text: translate(".pack.bronze.kart.desc2").value,
            },
            {
                spec: translate(".pack.bronze.kart.run3").value,
                text: translate(".pack.bronze.kart.desc3").value,
            },
        ],
    },
    {
        color: "bronze",
        icon: BronzeDrink,
        title: translate(".pack.bronze.drink.title").value,
        items: [
            { text: translate(".pack.bronze.drink.desc1").value },
            { text: translate(".pack.bronze.drink.desc2").value },
            { text: translate(".pack.bronze.drink.desc3").value },
            { text: translate(".pack.bronze.drink.desc4").value },
        ],
    },
    {
        color: "bronze",
        icon: BronzeFood,
        title: translate(".pack.bronze.food.title").value,
        items: [
            {
                spec: translate(".pack.bronze.food.desc1").value,
                hideDot: true,
                boldness: "bold",
            },
            { text: translate(".pack.bronze.food.desc2").value },
            { text: translate(".pack.bronze.food.desc3").value },
            { text: translate(".pack.bronze.food.desc4").value },
        ],
    },
    {
        color: "bronze",
        icon: BronzeReception,
        title: translate(".pack.bronze.reception.title").value,
        items: [
            { text: translate(".pack.bronze.reception.desc2").value },
            { text: translate(".pack.bronze.reception.desc3").value },
            { text: translate(".pack.bronze.reception.desc1").value },
        ],
    },
];
const racerInfoBlocks: PackInfoBlockProp[] = [
    {
        color: "primary",
        icon: RacerKart,
        title: translate(".pack.racer.kart.title").value,
        items: [
            { text: translate(".pack.racer.kart.feature").value, hideDot: true },
            {
                spec: translate(".pack.racer.kart.run1").value,
                text: translate(".pack.racer.kart.desc1").value,
            },
            {
                spec: translate(".pack.racer.kart.run2").value,
                text: translate(".pack.racer.kart.desc2").value,
            },
            {
                spec: translate(".pack.racer.kart.run3").value,
                text: translate(".pack.racer.kart.desc3").value,
            },
        ],
    },
    {
        color: "primary",
        icon: RacerDrink,
        title: translate(".pack.racer.drink.title").value,
        items: [{ text: translate(".pack.racer.drink.desc1").value }],
    },
    {
        color: "primary",
        icon: RacerReception,
        title: translate(".pack.bronze.reception.title").value,
        items: [
            { text: translate(".pack.racer.reception.desc1").value },
            { text: translate(".pack.racer.reception.desc2").value },
        ],
    },
];
</script>

<template>
    <VFlex flex-direction="column" class="has-background-dark">
        <EventUsNavigation current="enterprise"></EventUsNavigation>
        <ImageHeader secondary>
            <template #image>
                <img
                    :src="EventusEnterpriseHeader"
                    alt="Séminaire et team building karting électrique en entreprise" />
            </template>
            <template #title>
                <span>
                    <Translate path=".title"></Translate>
                </span>
            </template>
            <template #subtitle>
                <span class="has-text-white">
                    <Translate path=".subtitle"></Translate>&nbsp;
                    <span class="has-text-primary">
                        <Translate path=".subtitle2"></Translate>&nbsp;
                    </span>
                    <Translate path=".subtitle3"></Translate>&nbsp;
                    <span class="has-text-primary">
                        <Translate path=".subtitle4"></Translate>&nbsp;
                    </span>
                </span>
            </template>
        </ImageHeader>
        <Who>
            <template #title>
                <Translate path=".title"></Translate>
            </template>
            <template #content>
                <div class="mb-2">
                    <Translate path=".content"></Translate>
                </div>
                <div>
                    <Translate path=".content2"></Translate>
                </div>
            </template>
            <template #icon>
                <div style="display: flex">
                    <img
                        :src="IconEventusEnterprise"
                        alt="Icône team building entreprise karting" />
                    <button
                        class="button btn-size is-weight-bold is-size-6 is-success btn-primary-a11y is-font-mohol is-uppercase px-6 ml-6 my-auto"
                        @click="needContact('general')">
                        <div class="m-auto">
                            <Translate path=".moreInfos"></Translate>
                        </div>
                    </button>
                </div>
            </template>
            <template #titleP1>
                <Translate path=".titleP1"></Translate>
            </template>
            <template #titleP2>
                <Translate path=".titleP2"></Translate>
            </template>
        </Who>
        <VGallery class="m-5" :auto-height="true">
            <VGalleryPage class="py-6" :index="0">
                <div style="display: grid">
                    <PackCard color="gold" :head-image="GoldTitle">
                        <template #footer>
                            <div style="width: 100%" class="my-2 pack-footer-grid">
                                <SimpleButton
                                    class="my-auto mx-auto form-button"
                                    :color="ButtonColor.dark"
                                    :boldness="TextBoldness.bold"
                                    :size="ButtonSize.medium"
                                    outlined
                                    inverted
                                    @click="needContact('gold')">
                                    <Translate path=".pack.moreInfos"></Translate>
                                </SimpleButton>
                                <PackPrice
                                    class="is-uppercase my-auto ml-auto pack-price"
                                    :class="{ 'mx-auto': smallerThanSm }"
                                    style="font-weight: 900"
                                    :price="owner?.website.priceKartTeamGold"
                                    text-color="gold"
                                    text-size="6"
                                    text-font="secondary"
                                    price-color="white"
                                    price-size="2"
                                    price-font="sans-serif"
                                    is-tax-less />
                            </div>
                        </template>
                        <template #head>
                            <div style="width: 100%" class="pack-footer-grid">
                                <img
                                    :src="GoldTitle"
                                    alt="Formule team building karting"
                                    width="210"
                                    height="125"
                                    style="max-height: 125px; max-width: 210px"
                                    class="mr-auto" />
                            </div>
                        </template>
                        <template #content>
                            <div class="w-100 pack-content-grid p-3">
                                <PackInfoBlock
                                    v-for="info in goldInfoBlocks"
                                    :key="info.icon"
                                    :color="info.color"
                                    :icon="info.icon"
                                    :title="info.title"
                                    :items="info.items" />
                            </div>
                        </template>
                    </PackCard>
                </div>
            </VGalleryPage>
            <VGalleryPage class="py-6" :index="1">
                <div style="display: grid">
                    <PackCard color="silver" :head-image="SilverTitle">
                        <template #footer>
                            <div style="width: 100%" class="my-2 pack-footer-grid">
                                <SimpleButton
                                    class="my-auto mx-auto form-button"
                                    :color="ButtonColor.dark"
                                    :boldness="TextBoldness.bold"
                                    :size="ButtonSize.medium"
                                    outlined
                                    inverted
                                    @click="needContact('silver')">
                                    <Translate path=".pack.moreInfos"></Translate>
                                </SimpleButton>
                                <PackPrice
                                    class="is-uppercase my-auto ml-auto pack-price"
                                    :class="{ 'mx-auto': smallerThanSm }"
                                    style="font-weight: 900"
                                    :price="owner?.website.priceKartTeamSilver"
                                    text-color="silver"
                                    text-size="6"
                                    text-font="secondary"
                                    price-color="white"
                                    price-size="2"
                                    price-font="sans-serif"
                                    is-tax-less />
                            </div>
                        </template>
                        <template #head>
                            <img
                                :src="SilverTitle"
                                alt="pack"
                                width="210"
                                height="125"
                                style="max-height: 125px; max-width: 210px"
                                class="mr-auto" />
                        </template>
                        <template #content>
                            <TranslateNamespace path=".pack.silver">
                                <div class="w-100 pack-content-grid p-3">
                                    <PackInfoBlock
                                        v-for="info in silverInfoBlocks"
                                        :key="info.icon"
                                        :color="info.color"
                                        :icon="info.icon"
                                        :title="info.title"
                                        :items="info.items" />
                                </div>
                            </TranslateNamespace>
                        </template>
                    </PackCard>
                </div>
            </VGalleryPage>
            <VGalleryPage class="py-6" :index="2">
                <div style="display: grid">
                    <PackCard color="bronze" :head-image="BronzeTitle">
                        <template #footer>
                            <div style="width: 100%" class="my-2 pack-footer-grid">
                                <SimpleButton
                                    class="my-auto mx-auto form-button"
                                    :color="ButtonColor.dark"
                                    :boldness="TextBoldness.bold"
                                    :size="ButtonSize.medium"
                                    outlined
                                    inverted
                                    @click="needContact('bronze')">
                                    <Translate path=".pack.moreInfos"></Translate>
                                </SimpleButton>
                                <PackPrice
                                    class="is-uppercase my-auto ml-auto pack-price"
                                    :class="{ 'mx-auto': smallerThanSm }"
                                    style="font-weight: 900"
                                    :price="owner?.website.priceKartTeamBronze"
                                    text-color="bronze"
                                    text-size="6"
                                    text-font="secondary"
                                    price-color="white"
                                    price-size="2"
                                    price-font="sans-serif"
                                    is-tax-less />
                            </div>
                        </template>
                        <template #head>
                            <img
                                :src="BronzeTitle"
                                alt="pack"
                                width="210"
                                height="125"
                                style="max-height: 125px; max-width: 210px"
                                class="mr-auto" />
                        </template>
                        <template #content>
                            <TranslateNamespace path=".pack.silver">
                                <div class="w-100 pack-content-grid p-3">
                                    <PackInfoBlock
                                        v-for="info in bronzeInfoBlocks"
                                        :key="info.icon"
                                        :color="info.color"
                                        :icon="info.icon"
                                        :title="info.title"
                                        :items="info.items" />
                                </div>
                            </TranslateNamespace>
                        </template>
                    </PackCard>
                </div>
            </VGalleryPage>
            <VGalleryPage class="py-6" :index="3">
                <div style="display: grid">
                    <PackCard color="primary" :head-image="RacerTitle">
                        <template #footer>
                            <div style="width: 100%" class="my-2 pack-footer-grid">
                                <SimpleButton
                                    class="my-auto mx-auto form-button"
                                    :color="ButtonColor.dark"
                                    :boldness="TextBoldness.bold"
                                    :size="ButtonSize.medium"
                                    outlined
                                    inverted
                                    @click="needContact('racer')">
                                    <Translate path=".pack.moreInfos"></Translate>
                                </SimpleButton>
                                <PackPrice
                                    class="is-uppercase my-auto ml-auto pack-price"
                                    :class="{ 'mx-auto': smallerThanSm }"
                                    style="font-weight: 900"
                                    :price="owner?.website.priceKartTeamRacer"
                                    text-color="primary"
                                    text-size="6"
                                    text-font="secondary"
                                    price-color="white"
                                    price-size="2"
                                    price-font="sans-serif"
                                    is-tax-less />
                            </div>
                        </template>
                        <template #head>
                            <img
                                :src="RacerTitle"
                                alt="pack"
                                width="210"
                                height="125"
                                style="max-height: 125px; max-width: 210px"
                                class="mr-auto" />
                        </template>
                        <template #content>
                            <TranslateNamespace path=".pack.silver">
                                <div class="w-100 pack-content-grid p-3">
                                    <PackInfoBlock
                                        v-for="info in racerInfoBlocks"
                                        :key="info.icon"
                                        :color="info.color"
                                        :icon="info.icon"
                                        :title="info.title"
                                        :items="info.items" />
                                </div>
                            </TranslateNamespace>
                        </template>
                    </PackCard>
                </div>
            </VGalleryPage>
        </VGallery>
        <Footer></Footer>
        <FormContact
            v-model:open="isModalContactOpen"
            :block="contactBlock"></FormContact>
    </VFlex>
</template>

<style scoped lang="scss">
@use "sass:color";

.is-font-mohol {
    font-family: Mohol;
}

.w-100 {
    width: 100%;
}

.pack-footer-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    row-gap: 1rem;
    column-gap: 1rem;

    .form-button {
        grid-column: 2;
    }

    .pack-price {
        grid-column: 3;
    }
}

.pack-content-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    row-gap: 1rem;
    column-gap: 1rem;
}

@media screen and (max-width: 1360px) {
    .pack-content-grid {
        display: grid;
        grid-template-columns: repeat(1, 1fr);
        row-gap: 1rem;
        column-gap: 1rem;
        padding: 0 !important;
    }
}

.title-student {
    max-height: 125px;
    max-width: 210px;
}

.icon-block {
    max-width: 70px;
}

@media (max-width: 1280px) {
    .icon-block {
        max-height: 60px;
    }
}

@media (max-width: 700px) {
    .title-student {
        max-height: 90px;
    }

    .pack-footer-grid {
        grid-template-columns: repeat(1, 1fr) !important;
        column-gap: 1rem;
        row-gap: 3rem;

        .form-button {
            grid-column: 1;
        }

        .pack-price {
            grid-column: 1;
        }
    }
}

.btn-primary-a11y {
    background-color: hsl(153deg 100% 25%) !important;
    border-color: hsl(153deg 100% 25%) !important;
    color: white !important;
}
</style>
